import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Skeleton,
  Chip,
  IconButton,
  Stack,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import PageHeader from '../../components/layout/PageHeader';
import BeaconFormModal from '../../components/beacon/BeaconFormModal';
import api from '../../services/api';

const CONTAINER_SX = {
  maxWidth: 1600,
  margin: '0 auto',
  px: { xs: 2, md: 3 },
  py: 3,
};

const dataGridStyles = {
  border: 'none',
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#fafafa',
    borderBottom: '1px solid #eee',
  },
  '& .MuiDataGrid-columnSeparator': { display: 'none' },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
};

export default function Beacons() {
  const navigate = useNavigate();
  const [beacons, setBeacons] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [beaconToEdit, setBeaconToEdit] = useState(null);

  const openCreate = () => {
    setBeaconToEdit(null);
    setModalOpen(true);
  };

  const openEdit = (beacon) => {
    setBeaconToEdit(beacon);
    setModalOpen(true);
  };

  useEffect(() => {
    const isLogged = localStorage.getItem('token');
    if (!isLogged) navigate('/');
    else setIsAuthenticated(true);
  }, [navigate]);

  const loadBeacons = async () => {
    if (!isAuthenticated) return;
    try {
      const response = await api.get('/beacons');
      const sorted = [...response.data].sort((a, b) => (
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      ));
      setBeacons(sorted);
    } catch (err) {
      console.error(err);
      alert('Falha ao carregar beacons, tente recarregar a página.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadBeacons();
  }, [isAuthenticated]);

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja apagar esse beacon?')) return;
    try {
      await api.delete(`/beacons/${id}`);
      alert('Beacon apagado com sucesso.');
      loadBeacons();
    } catch (err) {
      console.error(err);
      alert('Falha ao apagar beacon, tente novamente.');
    }
  };

  const rows = beacons.map((b) => ({
    id: b.id,
    name: b.name,
    brand: b.brand,
    type: b.type,
    identifier: b.type !== 'eddystone'
      ? `${b.deviceId} · ${b.major}:${b.minor}`
      : `${b.namespace} · ${b.instanceId}`,
    raw: b,
  }));

  const columns = [
    {
      field: 'name', headerName: 'Nome', flex: 1, minWidth: 140,
    },
    {
      field: 'brand',
      headerName: 'Marca',
      width: 130,
      renderCell: (p) => (
        <Chip label={p.value} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
      ),
    },
    {
      field: 'type',
      headerName: 'Tipo',
      width: 130,
      renderCell: (p) => <Chip label={p.value} size="small" variant="outlined" />,
    },
    {
      field: 'identifier',
      headerName: 'Identificação',
      flex: 2,
      minWidth: 260,
      renderCell: (p) => (
        <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{p.value}</span>
      ),
    },
    {
      field: 'action',
      headerName: 'Ações',
      width: 120,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      disableColumnMenu: true,
      renderCell: (p) => (
        <Stack direction="row" spacing={0.5}>
          <IconButton
            size="small"
            aria-label="Editar"
            onClick={() => openEdit(p.row.raw)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Excluir"
            onClick={() => handleDelete(p.row.id)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={CONTAINER_SX}>
      <PageHeader
        title="Beacons"
        description="Dispositivos Bluetooth cadastrados no sistema. Cada beacon pode ser associado a um ponto de uma exposição pra ativar conteúdo por proximidade."
        action={(
          <Button
            variant="contained"
            color="success"
            startIcon={<AddIcon />}
            onClick={openCreate}
          >
            Novo beacon
          </Button>
        )}
      />

      <Paper elevation={1} sx={{ borderRadius: 2, border: '1px solid #eee', overflow: 'hidden' }}>
        {isLoading ? (
          <Stack spacing={1} sx={{ p: 2 }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} variant="rectangular" height={48} />
            ))}
          </Stack>
        ) : (
          <DataGrid
            sx={dataGridStyles}
            autoHeight
            rows={rows}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
          />
        )}
      </Paper>

      <BeaconFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        beaconToEdit={beaconToEdit}
        onSaved={loadBeacons}
      />
    </Box>
  );
}
